import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AppContext } from 'contexts/AppContext'
import { useNavigate, useParams } from 'react-router-dom'
import { EmissionScheduleRouteParams } from '../../../routes'
import Service from './service'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, store } from '../../../store'
import PageHeader from '../../../components/Layout/PageHeader'
import TooltipHelp from '../../../components/TooltipHelp'
import { useTranslation } from 'react-i18next'
import CardHeader from '../../../components/Layout/CardHeader'
import { CampaignEmissionScheduleTooltip } from '../../Campaign/EmissionSchedule/components/campaign-emission-schedule-tooltip'
import Card, { CardStatus } from '../../../components/Layout/Card'
import { voohCreationEmissionActions } from './slice'
import VoohCreationFormSchedule from './VoohCreationFormSchedule/VoohCreationFormSchedule'
import {
  VoohMediaCheckboxTreeElement,
  VoohMediaFormat,
  VoohMediaNode,
} from '../VoohCampaignForm/types'
import { Medium, MediumPlacement, MediumStatus, VOOH_OFFER } from '../../../types/campaign'
import { voohCampaignFormActions } from '../VoohCampaignForm/slice'
import voohMediaSelectionService from '../VoohMediaSelection/service'
import VoohMediaSelection from '../VoohMediaSelection/VoohMediaSelection'
import Input, { InputType } from '../../../components/Form/Input'
import MediaMap from '../../../components/MediaMap'
import FlexRow from '../../../components/Layout/FlexRow'
import FlexCol from '../../../components/Layout/FlexCol'
import Preview from '../../../components/Preview'
import './VoohEmissionSchedule.scss'
import debounce from 'lodash/debounce'
import VoohCampaignFormService from '../VoohCampaignForm/service'
import Button from '../../../components/Form/Button'

const DEBOUNCE_POST_DELAY = 1000 // [ms]

const EmissionSchedule: React.FC = () => {
  const { creationId: urlCreationId, readOnly } = useParams<EmissionScheduleRouteParams>()
  const {
    schedules,
    file,
    creationId,
    locations,
    frequency,
    map,
    details,
    duration,
    thumbnailUrl,
  } = useSelector((state: RootState) => state.voohCreationEmission)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { userData } = useContext(AppContext)
  const [nodes, setNodes] = useState([])
  const [filteredNodes, setFilteredNodes] = useState<VoohMediaCheckboxTreeElement[]>([])
  const [filterNodesText, setFilterNodesText] = useState('')
  const [checkedNodes, setCheckedNodes] = useState<string[]>([])
  const [filterByIds, setFilterByIds] = useState<string | null>(null)
  const [appendFilterByIds, setAppendFilterByIds] = useState<boolean>(false)
  const [mapMedia, setMapMedia] = useState<Medium[]>([])
  const navigate = useNavigate()

  const flattenedMediaTreeNodeValues: string[] = []
  let mediaSize = 0
  let checkedMediaOnFilterById: string[] = []

  const debouncedCreationUpdate = useCallback(
    debounce(VoohCampaignFormService.updateCreation, DEBOUNCE_POST_DELAY),
    []
  )

  const debouncedMediaSelectionUpdate = useCallback(
    debounce(Service.updateVoohCreationMedia, DEBOUNCE_POST_DELAY),
    []
  )

  useEffect(
    () => () => {
      store.dispatch(voohCreationEmissionActions.resetVoohCreationScheduleState())
    },
    []
  )

  useEffect(() => {
    if (urlCreationId) {
      void Service.getVoohCreationEmissionData(urlCreationId)
    }
  }, [urlCreationId])

  useEffect(() => {
    if (!creationId) {
      return
    }

    const selectedVoohMediaNodes: VoohMediaNode[] = []
    JSON.stringify(locations.mediums, (_, nestedValue) => {
      if (
        nestedValue &&
        !nestedValue?.children &&
        checkedNodes.some(el => el.split('_')[0] === nestedValue.id?.toString())
      ) {
        selectedVoohMediaNodes.push({ ...nestedValue })
      }
      return nestedValue
    })

    setMapMedia(
      selectedVoohMediaNodes.map((voohMediaNode: VoohMediaNode) => {
        return {
          address: voohMediaNode.address || 'to be added',
          agglomeration: {
            id: voohMediaNode.agglomeration_id?.toString() || '',
            name: voohMediaNode.agglomeration?.name || '',
          },
          asId: voohMediaNode.asId || '',
          building: {
            id: voohMediaNode.building_id?.toString() || '',
            name: voohMediaNode.name,
          },
          city: {
            id: voohMediaNode.city_id?.toString() || '',
            name: voohMediaNode.city?.name || '',
          },
          comment: '',
          id: voohMediaNode.id.toString(),
          images: [],
          lat: parseInt(voohMediaNode.latitude || ''),
          lon: parseInt(voohMediaNode.longitude || ''),
          mediaFormat: VoohMediaFormat.MOVE_TV,
          placement: MediumPlacement.INDOOR,
          screenCount: 1,
          status: voohMediaNode.status || MediumStatus.NOT_OPERATING,
          used: true,
          vac: 1,
          poiCategories: [],
          creationFormat: 'video',
        }
      })
    )

    if (locations.show) {
      void debouncedMediaSelectionUpdate(
        creationId,
        selectedVoohMediaNodes.map(selectedMedia => {
          return {
            id: selectedMedia.id,
            used: true,
          }
        })
      )
    }

    store.dispatch(voohCampaignFormActions.setSelectedVoohMedia(selectedVoohMediaNodes))
  }, [checkedNodes])

  useEffect(() => {
    if (locations.mediums) {
      mediaSize = 0
      const mediaTreeCheckboxNodes = voohMediaSelectionService.buildMediaTreeCheckboxNodes(
        locations.mediums,
        VOOH_OFFER.MEDIA_SELECTION,
        userData.user?.roles,
        flattenedMediaTreeNodeValues,
        mediaSize,
        t
      )
      setTimeout(() => {
        setNodes(mediaTreeCheckboxNodes)
        setFilteredNodes([
          {
            label: t('form.campaign.details.voohMediaNodes.allClubs'),
            value: 'all',
            children: mediaTreeCheckboxNodes,
          },
        ])
        setCheckedNodes(
          flattenedMediaTreeNodeValues.filter(el =>
            locations.selectedMediums.some(media => media.id.toString() === el.split('_')[0])
          )
        )
      })
    }
  }, [locations.mediums])

  useEffect(() => {
    if (!filterNodesText) {
      setFilteredNodes([
        {
          label: t('form.campaign.details.voohMediaNodes.allClubs'),
          value: 'all',
          children: nodes,
        },
      ])
      return
    } else {
      setFilteredNodes([
        {
          label: t('form.campaign.details.voohMediaNodes.allClubs'),
          value: 'all',
          children: nodes.reduce(voohMediaSelectionService.filterNodes(filterNodesText), []),
        },
      ])
    }
  }, [filterNodesText])

  useEffect(() => {
    if (filterByIds !== null) {
      checkedMediaOnFilterById = []
      if (filterByIds === '') {
        if (!appendFilterByIds) {
          setCheckedNodes([])
        }
        return
      }
      nodes.reduce(
        voohMediaSelectionService.filterNodesByIds(filterByIds, checkedMediaOnFilterById),
        []
      )
      if (appendFilterByIds) {
        // Append new selections to existing ones (avoiding duplicates)
        setCheckedNodes(prev => [...new Set([...prev, ...checkedMediaOnFilterById])])
      } else {
        setCheckedNodes(checkedMediaOnFilterById)
      }
    }
  }, [filterByIds, appendFilterByIds])

  return (
    <div className='EmissionSchedule'>
      <div className='EmissionSchedule__header'>
        <PageHeader>
          {t(`${readOnly ? 'common.emissionSchedule' : 'emissionSchedule.titleEdit'}`) +
            ' - ' +
            file.name}
        </PageHeader>

        {!readOnly && (
          <TooltipHelp
            className='EmissionSchedule__tooltip'
            containerClassName='EmissionSchedule__tooltip--a'
          >
            {t('emissionSchedule.info')}
          </TooltipHelp>
        )}
      </div>

      <Card>
        <CardHeader
          number={1}
          collapsed={!schedules.show}
          onCollapse={() => {
            dispatch(voohCreationEmissionActions.toggleShow('schedules'))
          }}
        >
          <div className='EmissionSchedule__header'>
            {t('emissionSchedule.duration')}
            {!readOnly && <CampaignEmissionScheduleTooltip />}
          </div>
        </CardHeader>

        {schedules.show && (
          <div className='EmissionSchedule__schedule'>
            <VoohCreationFormSchedule readOnly={!!readOnly} />
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          number={2}
          collapsed={!locations.show}
          onCollapse={() => {
            dispatch(voohCreationEmissionActions.toggleShow('locations'))
          }}
          status={locations.loading ? CardStatus.LOADING : CardStatus.NONE}
        >
          {t('common.locations')}
        </CardHeader>

        <div
          className={
            locations.show ? 'EmissionSchedule__locations' : 'EmissionSchedule__locations--hidden'
          }
        >
          <VoohMediaSelection
            readonly={!!readOnly}
            filterByIds={filterByIds}
            setFilterByIds={setFilterByIds}
            filterNodesText={filterNodesText}
            setFilterNodesText={setFilterNodesText}
            setCheckedNodes={setCheckedNodes}
            filteredNodes={filteredNodes}
            checked={checkedNodes}
            appendFilterByIds={appendFilterByIds}
            setAppendFilterByIds={setAppendFilterByIds}
          />
        </div>
      </Card>

      <Card>
        <CardHeader
          number={3}
          collapsed={!frequency.show}
          onCollapse={() => {
            dispatch(voohCreationEmissionActions.toggleShow('frequency'))
          }}
          status={frequency.loading ? CardStatus.LOADING : CardStatus.NONE}
        >
          {t('form.campaign.creations.frequency')}
        </CardHeader>
        {frequency.show && (
          <>
            <FlexRow className='CreationsFormEmissionNumber--row'>
              <FlexCol>
                <Preview
                  className={'CreationsFormEmissionNumber--preview'}
                  src={file.url}
                  thumbnailSrc={thumbnailUrl}
                  isVideo={false}
                  alt={file.name}
                />
              </FlexCol>

              <FlexCol>
                <div className={'CreationsFormEmissionNumber__emissions'}>
                  <div className={'CreationsFormEmissionNumber__emissions--label'}>
                    <span>
                      {t('form.campaign.creations.creationHourFrequency')}:&nbsp;
                      <b>{file.name}</b>
                    </span>
                  </div>
                  <Input
                    id='frequency'
                    disabled={!!readOnly}
                    type={InputType.NUMBER}
                    title={t('form.campaign.creations.frequency')}
                    value={frequency.hourFrequency}
                    onChange={value => {
                      dispatch(voohCreationEmissionActions.updateVoohCreationFrequency(value))
                      debouncedCreationUpdate(
                        {
                          id: creationId,
                          details: details || '',
                          duration,
                          hourFrequency: parseInt(value),
                        },
                        VoohMediaFormat.MOVE_TV
                      )
                    }}
                  />
                </div>
              </FlexCol>
            </FlexRow>
          </>
        )}
      </Card>

      <Card
        headerNumber={4}
        title={t('common.placementOfMedia')}
        isCollapsed={!map.show}
        onCollapse={() => {
          dispatch(voohCreationEmissionActions.toggleShow('map'))
        }}
        status={CardStatus.NONE}
        noMarginBody
      >
        {map.show && (
          <MediaMap
            className='EmissionSchedule__map'
            media={mapMedia}
            withClustering
          />
        )}
      </Card>

      <Button
        className='EmissionSchedule__button'
        onClick={() => void navigate(-1)}
      >
        {readOnly ? t('common.back') : t('common.save')}
      </Button>
    </div>
  )
}

export default EmissionSchedule
